import { IValues, TGender } from './types'
import { annuityTable, employerOneTimeContribution, maxExperiences, minAge, retirementAges } from './data'

/**
 * Функция для перевода процента в дробь
 * @param percent - значение в процентах
 */
export const percentToFraction = (percent: number) => percent / 100

const MINIMUM_CONTRIBUTION = 500
const MINIMUM_EMPLOYER_CONTRIBUTION = 750
const MAXIMUM_EMPLOYER_CONTRIBUTION = 5000
const INCOME_RATE_PERCENT = 4 //Индикативная ставка доходности, применяемая в расчете, в процентах
const FIRST_PAYMENT_SIZE_PERCENT = 25
const YEARS_OF_PAYMENTS = 5
const PAYMENTS_PER_YEAR = 12
const MIN_LIFETIME_PENSION = 700

const monthlyIncomeRatePercent = ((percentToFraction(INCOME_RATE_PERCENT) + 1) ** (1 / 12) - 1) * 100

/**
 * Функция для проведения всех нужных расчетов
 * @param values - все значения вводимые пользователем, интерфейс IValues
 */

export const getCalculatedData = (values: IValues) => {
  const baseZeroing = {
    warning: '',
    contributionSize : 0,
    contributionSizePercent : 0,
    employerContributionSize : 0,
    employerContributionSizePercent : 0,
  }
  const advanceZeroing = {
    retirementAge : 0,
    yearsBeforeRetirement : 0,
    minimumContributionPercent : 0,
    savingsOnIPS : 0,
    ipsContributionsPaid : 0,
    ipsAccruedInvestmentIncome : 0,
    ipsFirstPayment : 0,
    ipsPensionAmount : 0,
    ipsPensionAmountAlt : 0,
    savingsOnKIPS : 0,
    kipsContributionsPaid : 0,
    kipsAccruedInvestmentIncome : 0,
    kipsFirstPayment : 0,
    kipsPensionAmount : 0,
    kipsPensionAmountAlt : 0,
    numberOfSalaries : 0,
    estimatedYearsOfWork : 0,
    kepvOnetimeContribution : 0,
    kepvPensionAmount : 0,
    kepvFirstPayment : 0,
    kepvPensionAmountAlt : 0,
  }

  const zeroing = {
    ...baseZeroing,
    ...advanceZeroing,
  }

  const result = {...zeroing}

  const { age, gender, salary, income, percentContribution, exp } = values

  const employerContributionPercent = getEmployerContributionPercent(percentContribution)

  result.retirementAge = retirementAges[gender]
  result.yearsBeforeRetirement = getYearsBeforeRetirement(result.retirementAge, age)
  result.minimumContributionPercent = getMinimumContributionPercent(result.yearsBeforeRetirement)
  result.contributionSize = getContributionSize(percentContribution, income)
  result.contributionSizePercent = getContributionSizePercent(result.contributionSize, income)
  result.employerContributionSize = getEmployerContributionSize(employerContributionPercent, income)
  result.employerContributionSizePercent = getEmployerContributionSizePercent(result.employerContributionSize, income)

  result.estimatedYearsOfWork = result.retirementAge - age
  const totalExperience = result.estimatedYearsOfWork + exp

  if (result.yearsBeforeRetirement > 0) {
    result.savingsOnIPS = getSavings(
      result.contributionSize,
      result.yearsBeforeRetirement,
      monthlyIncomeRatePercent,
      employerContributionPercent,
    )
    result.savingsOnKIPS = getSavings(result.employerContributionSize, result.yearsBeforeRetirement, monthlyIncomeRatePercent)

    result.ipsContributionsPaid = getСontributionsPaid(result.yearsBeforeRetirement, result.contributionSize)
    result.ipsAccruedInvestmentIncome = getAccruedInvestmentIncome(result.savingsOnIPS, result.ipsContributionsPaid)

    result.ipsPensionAmount = getIPSPensionAmount(getMinPensionCalcPath(result.savingsOnIPS))
    if (result.ipsPensionAmount >= MIN_LIFETIME_PENSION) {
      result.ipsFirstPayment = getFirstPayment(result.savingsOnIPS)
      result.ipsPensionAmountAlt = getIPSPensionAmountAlt(result.savingsOnIPS)
    } else {
      result.ipsPensionAmount = 0
    }

    const kipsAnnuity = getKIPSAnnuity(gender, result.retirementAge)

    result.kipsContributionsPaid = getСontributionsPaid(result.yearsBeforeRetirement, result.employerContributionSize)
    result.kipsAccruedInvestmentIncome = getAccruedInvestmentIncome(result.savingsOnKIPS, result.kipsContributionsPaid)

    result.kipsPensionAmount = getKIPSPensionAmount(getMinPensionCalcPath(result.savingsOnKIPS), kipsAnnuity)
    if (result.kipsPensionAmount >= MIN_LIFETIME_PENSION) {
      result.kipsFirstPayment = getFirstPayment(result.savingsOnKIPS)
    } else {
      result.kipsPensionAmount = 0
    }

    result.kipsPensionAmountAlt = getKIPSPensionAmountAlt(result.savingsOnKIPS, kipsAnnuity)
    result.kipsPensionAmountAlt = result.kipsPensionAmountAlt >= MIN_LIFETIME_PENSION ? result.kipsPensionAmountAlt : 0

    result.numberOfSalaries = getNumberOfSalaries(gender, totalExperience)
    result.kepvOnetimeContribution = getKEPVOnetimeContribution(totalExperience, result.numberOfSalaries, salary)

    result.kepvPensionAmount = getKEPVPensionAmount(getMinPensionCalcPath(result.kepvOnetimeContribution))
    if (result.kepvPensionAmount >= MIN_LIFETIME_PENSION) {
      result.kepvFirstPayment = getFirstPayment(result.kepvOnetimeContribution)
      result.kepvPensionAmountAlt = getKEPVPensionAmount(result.kepvOnetimeContribution, true)
    } else {
      result.kepvPensionAmount = 0
    }
  }

  const incomeNotEnought = income <= 999
  const expNotEnought = exp < 1

  if(incomeNotEnought && expNotEnought) return {...zeroing, warning: 'Ваша зарплата ниже прожиточного минимума и стаж работы во ВНИИЭФ должен быть не менее 1 года для вступления в КПП'}
  if(incomeNotEnought) return {...result, ...baseZeroing, warning: 'Ваша зарплата ниже прожиточного минимума, необходимо ввести другую цифру'}
  if(expNotEnought) return {...result, ...advanceZeroing, warning: 'Стаж работы во ВНИИЭФ для вступления в КПП должен быть не менее 1 года'}

  return result
}

/**
 * Сумма единовременного взноса на КЕПВ
 * @param totalExperience - суммарный стаж
 * @param numberOfSalaries - количество окладов
 * @param salary - оклад
 */
const getKEPVOnetimeContribution = (totalExperience: number, numberOfSalaries: number, salary: number) => {
  return totalExperience >= 5 ? numberOfSalaries * salary : 0
}

/**
 * Количество окладов в соответствии с Положением НПО Предприятия к  пенсионному возрасту
 * @param gender - пол
 * @param totalExperience - суммарный стаж
 */
const getNumberOfSalaries = (gender: TGender, totalExperience: number) => {
  let result = 0
  for (const contribution of employerOneTimeContribution) {
    if (totalExperience >= contribution[gender]) {
      result = contribution.contribution
    }
    if (totalExperience < contribution[gender]) break
  }
  return result
}

/**
 * Функция первого этапа для расчета минимальной пенсии
 * @param savings - Накопления к пенсионному возрасту
 */
const getMinPensionCalcPath = (savings: number) => {
  return savings * (1 - percentToFraction(FIRST_PAYMENT_SIZE_PERCENT))
}

/**
 * Ануитет по таблице
 * @param gender - пол
 * @param retirementAge - возраст выхода на пенсию
 */
const getKIPSAnnuity = (gender: TGender, retirementAge: number) => {
  return annuityTable[retirementAge][gender]
}

/**
 * Размер пенсии при втором варианте ИПС
 * @param savingsOnIPS - Накопления на ИПС к пенсионному возрасту
 */
const getIPSPensionAmountAlt = (savingsOnIPS: number) => {
  return savingsOnIPS / (YEARS_OF_PAYMENTS * PAYMENTS_PER_YEAR)
}

/**
 * Размер пенсии при первом варианте ИПС
 * @param minPensionCalcPath - Расчет с первого этапа
 */
const getIPSPensionAmount = (minPensionCalcPath: number) => {
  return minPensionCalcPath / (YEARS_OF_PAYMENTS * PAYMENTS_PER_YEAR - 1)
}

/**
 * Размер пенсии при первом варианте КИПС
 * @param minPensionCalcPath - Расчет с первого этапа
 * @param kipsAnnuity - Ануитет по таблице
 */
const getKIPSPensionAmount = (minPensionCalcPath: number, kipsAnnuity: number) => {
  return minPensionCalcPath / kipsAnnuity
}

/**
 * К пенсионному возрасту сформирована ежемесячная срочная пенсия - на оставшийся срок - 59 месяцев
 * @param minPensionCalcPath - Расчет с первого этапа
 */
const getKEPVPensionAmount = (minPensionCalcPath: number, full = false) => {
  return minPensionCalcPath / (full ? 60 : 59)
}

/**
 * Размер пенсии при втором варианте КИПС
 * @param savings - Накопления к пенсионному возрасту
 * @param kipsAnnuity - Ануитет по таблице
 */
const getKIPSPensionAmountAlt = (savings: number, kipsAnnuity: number) => {
  return savings / kipsAnnuity
}

/**
 * Размер первой выплаты при первом варианте
 * @param available - Выходит ли минимальная пенсия
 * @param savings - Накопления к пенсионному возрасту
 */
const getFirstPayment = (savings: number) => {
  return savings * percentToFraction(FIRST_PAYMENT_SIZE_PERCENT)
}

/**
 * Начислено инвестиционного дохода
 * @param savings - Накопления к пенсионному возрасту
 * @param contributionsPaid - Уплачено взносов
 */
const getAccruedInvestmentIncome = (savings: number, contributionsPaid: number) => {
  return savings - contributionsPaid
}

/**
 * Уплачено взносов
 * @param yearsBeforeRetirement - лет до пенсии
 * @param contributionSize - расчетный взнос
 */
const getСontributionsPaid = (yearsBeforeRetirement: number, contributionSize: number) => {
  return contributionSize * Math.trunc(yearsBeforeRetirement / (1 / 12))
}

/**
 * Будет накоплено к  пенсионному возрасту, при указании employerContributionPercent будет расчет для ИПС, без - для КИПС
 * @param employerContributionSize - расчетный взнос работодателя
 * @param yearsBeforeRetirement - лет до пенсии
 * @param monthlyIncomeRatePercent - месячная ставка доходности
 * @param employerContributionPercent - взнос работадателя (в процентах)
 *
 */

const getSavings = (
  contributionSize: number,
  yearsBeforeRetirement: number,
  monthlyIncomeRatePercent: number,
  employerContributionPercent?: number,
) => {
  const monthlyIncomeRate = percentToFraction(monthlyIncomeRatePercent)
  const tYearsBeforeRetirement = Math.trunc(yearsBeforeRetirement)

  let yearsOrContribution
  let tYearsOrContribution

  if (employerContributionPercent) {
    yearsOrContribution = percentToFraction(employerContributionPercent)
    tYearsOrContribution = percentToFraction(Math.trunc(employerContributionPercent))
  } else {
    yearsOrContribution = yearsBeforeRetirement
    tYearsOrContribution = tYearsBeforeRetirement
  }

  const path1 = contributionSize * (1 + monthlyIncomeRate)
  const path2 = 1 - (1 + monthlyIncomeRate) ** (tYearsBeforeRetirement * 12)
  const path3 = 1 - (1 + monthlyIncomeRate)
  const path4 = Math.trunc((yearsOrContribution - tYearsOrContribution) / (1 / 12))

  return (path1 * path2) / path3 + path4 * contributionSize
}

/**
 * Функция для получения лет до пенсии
 * @param retirementAge - срок выхода на пенсию
 * @param age - возраст
 */
const getYearsBeforeRetirement = (retirementAge: number, age: number) =>
  retirementAge - age >= 0 ? retirementAge - age : 0

/**
 * Минимальный взнос, удерживаемый из заработной платы в соответствии с Положением НПО Предприятия, в % от зарплаты
 * @param yearsBeforeRetirement - лет до пенсии
 */
const getMinimumContributionPercent = (yearsBeforeRetirement: number) => {
  //Соответствия Возраст до пенсии/размер взноса
  const minimumContributionMap: [number, number][] = [
    [0, 5],
    [6, 3],
    [10, 2],
    [20, 1],
    [30, 0.8],
  ]

  let result = minimumContributionMap[0][1]
  for (const contribution of minimumContributionMap) {
    if (yearsBeforeRetirement >= contribution[0]) {
      result = contribution[1]
    }
  }
  return result
}

/**
 * Размер ежемесячного пенсионного взноса работодателя в процентах
 * @param percentContribution - взнос (в процентах)
 */
const getEmployerContributionPercent = (percentContribution: number) => {
  //Соответсвия взноса со взносом работодателя
  const contributionMap: [number, number][] = [
    [0.8, 1],
    [1, 1.5],
    [2, 2.5],
    [3, 3.5],
    [4, 4.5],
    [5, 5.5],
  ]

  let result = contributionMap[0][1]
  for (const contribution of contributionMap) {
    if (percentContribution >= contribution[0]) {
      result = contribution[1]
    }
  }
  return result
}

/**
 * Функция для расчета размера ежемесячного пенсионного взноса работника-участника программы. Если результат окажется меньше, вернет минимальный
 * @param percentContribution - взнос (в процентах)
 * @param income - заработная плата
 */
const getContributionSize = (percentContribution: number, income: number) => {
  const result = percentToFraction(percentContribution) * income
  return result >= MINIMUM_CONTRIBUTION ? result : MINIMUM_CONTRIBUTION
}

/**
 * Расчетный размер ежемесячного пенсионного взноса Вкладчика — Предприятия, руб
 * @param employerContributionPercent - взнос работадателя (в процентах)
 * @param income - заработная плата
 */
const getEmployerContributionSize = (employerContributionPercent: number, income: number) => {
  const result = percentToFraction(employerContributionPercent) * income
  return result >= MINIMUM_EMPLOYER_CONTRIBUTION
    ? result <= MAXIMUM_EMPLOYER_CONTRIBUTION
      ? result
      : MAXIMUM_EMPLOYER_CONTRIBUTION
    : MINIMUM_EMPLOYER_CONTRIBUTION
}

/**
 * Расчетный размер  ежемесячного пенсионного взноса работника-участника программы, % от начисленной заработной платы.
 * @param contributionSize - расчетный взнос
 * @param income - заработная плата
 */
const getContributionSizePercent = (contributionSize: number, income: number) => {
  return (contributionSize / income) * 100
}

/**
 * Расчетный размер  ежемесячного пенсионного взноса Вкладчика — Предприятия, % от начисленной заработной платы Участника (макс. взнос Предприятия - 5,5% от
 * зарплаты Участника)
 * @param employerContributionSize - расчетный взнос работодателя
 * @param income - заработная плата
 */
const getEmployerContributionSizePercent = (employerContributionSize: number, income: number) => {
  return (employerContributionSize / income) * 100
}
